import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout } from '../../store/actions/authActions';

// How long a user can go without any interaction before being signed out.
export const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
// How often to check elapsed idle time. Kept short relative to IDLE_TIMEOUT_MS
// since the check compares wall-clock timestamps, so it stays correct even
// through throttled background timers or a laptop sleep/wake cycle.
const CHECK_INTERVAL_MS = 15_000;
// Activity events fire far more often than we need to react to them.
const ACTIVITY_THROTTLE_MS = 5_000;

export const LAST_ACTIVITY_KEY = 'lastActivityAt';
export const IDLE_LOGOUT_REASON_KEY = 'authLogoutReason';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

const readSession = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, quota, etc.) — in-memory tracking still works for this tab.
  }
};

const removeSession = (key: string) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/**
 * Signs the user out after IDLE_TIMEOUT_MS of no interaction, regardless of
 * whether the underlying Firebase session could otherwise be silently
 * refreshed. This is a deliberate idle-session policy, separate from the
 * axios 401 refresh-and-retry handling in services/api/config.ts.
 */
export default function IdleTimeoutManager() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const lastActivityRef = useRef<number>(Date.now());
  const loggedOutRef = useRef(false);

  const recordActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) {
      return;
    }
    lastActivityRef.current = now;
    writeSession(LAST_ACTIVITY_KEY, String(now));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      loggedOutRef.current = false;
      return;
    }

    const stored = readSession(LAST_ACTIVITY_KEY);
    lastActivityRef.current = stored ? Number(stored) : Date.now();

    const checkIdle = () => {
      if (loggedOutRef.current) {
        return;
      }

      if (Date.now() - lastActivityRef.current >= IDLE_TIMEOUT_MS) {
        loggedOutRef.current = true;
        removeSession(LAST_ACTIVITY_KEY);
        writeSession(IDLE_LOGOUT_REASON_KEY, 'idle');
        dispatch(logout());
      }
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, recordActivity, { passive: true }));
    document.addEventListener('visibilitychange', checkIdle);
    window.addEventListener('focus', checkIdle);

    const intervalId = window.setInterval(checkIdle, CHECK_INTERVAL_MS);

    // Catches the case where the tab was already idle past the threshold
    // before this effect ran (e.g. app just finished restoring the session).
    checkIdle();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, recordActivity));
      document.removeEventListener('visibilitychange', checkIdle);
      window.removeEventListener('focus', checkIdle);
      window.clearInterval(intervalId);
    };
  }, [dispatch, isAuthenticated, recordActivity]);

  return null;
}
